<ng-container *transloco="let t">
    <!-- begin:: Minimized Dock -->
    @if (svc.open() && svc.minimized()) {
    <button type="button" class="chat-dock" (click)="svc.restorePanel()" aria-label="Restore chat panel">
        {{ svc.companyName() }}
        @if (svc.unreadCount() > 0) {
        <span class="dock-badge" aria-label="{{ svc.unreadCount() }} unread">
            {{ svc.unreadCount() > 99 ? '99+' : svc.unreadCount() }}
        </span>
        }
    </button>
    }
    <!-- end:: Minimized Dock -->

    <!-- begin:: Full Panel View -->
    @if (svc.isFullPanelVisible()) {
    <section class="chat-panel" role="complementary" aria-label="Chat user list">
        <header class="chat-header">
            <div class="chat-title" [title]="headerTitle()">
                @if (svc.isChatView()) {
                <button type="button" class="icon-btn light" (click)="onBack()" aria-label="Back to users">
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none">
                        <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
                <span class="presence-dot header" [attr.data-status]="svc.selectedPresence()"></span>
                }
                <span class="title-text">{{ headerTitle() }}</span>
                @if (svc.isChatView() && svc.selectedPresence()) {
                <span class="presence-label">{{ svc.selectedPresence() }}</span>
                }
            </div>

            <div class="chat-actions">
                <button type="button" class="icon-btn" (click)="svc.minimizePanel()" aria-label="Minimize">
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                        <path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                </button>
                <button type="button" class="icon-btn" (click)="onClose()" aria-label="Close">
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                </button>
            </div>
        </header>

        <!-- LIST VIEW -->
        @if (svc.isListView()) {
        <div class="chat-body">
            @if (svc.loadingUsers()) {
            <!-- Skeleton -->
            <ul class="user-list skeleton" aria-hidden="true">
                @for (i of [0,1,2,3,4]; track i) {
                <li class="user-row skeleton-row">
                    <span class="avatar-skel"></span>
                    <span class="line-skel w-60"></span>
                    <span class="line-skel w-24"></span>
                    <span class="chevron-skel"></span>
                </li>
                }
            </ul>
            } @else {
            <ul class="user-list" role="list">
                @for (u of svc.users(); track u.id) {
                <li class="user-row" [class.disabled]="!svc.canChat(u)" [attr.aria-disabled]="!svc.canChat(u)"
                    [attr.tabindex]="svc.canChat(u) ? 0 : -1" [attr.role]="svc.canChat(u) ? 'button' : 'listitem'"
                    (click)="openChatWith(u)">
                    <span class="presence-dot" [attr.data-status]="u.presence"></span>
                    <span class="user-name">{{ u.name }}</span>
                    <span class="user-meta">{{ u.presence }}</span>
                    <span class="chevron" aria-hidden="true">›</span>
                </li>
                } @empty {
                <li class="p-5 fw-bold" transloco="sentence_no_representative_available"></li>
                }
            </ul>
            }
        </div>
        }

        <!-- CHAT VIEW -->
        @if (svc.isChatView()) {
        <div class="chat-body chat-view">
            <div class="chat-banner">
                <span class="me-1" transloco="label_chatting_with"></span>
                <strong>{{ svc.selectedUser()?.name }}</strong>
                @if(svc.company()) {
                <span class="company-pill"> <span class="text-lowercase" transloco="label_from"></span>
                    {{ svc.company()!.name }}
                </span>
                }
            </div>

            <!-- Gate -->
            @if (!chatStarted()) {
            <form [formGroup]="customerForm" (ngSubmit)="startChat()" class="gate-form" novalidate name="chatGateForm" role="form" aria-label="Start chat form">
                <div class="field">
                    <label for="custName" transloco="label_your_name"></label>
                    <input id="custName" type="text" formControlName="name" placeholder="e.g., John Doe" autocomplete="name" />
                    @if (touched('name') && invalid('name')) { <small class="err"
                        transloco="label_required_field"></small> }
                </div>
                <button class="primary" type="submit" [disabled]="customerForm.invalid"
                    transloco="label_start_chat"></button>
            </form>
            } @else {
            <!-- Conversation area -->
            <div #messagesEl class="messages" aria-live="polite">

                <!-- Render chat messages with alignment + time -->
                @for (m of svc.messages(); track m.id) {
                <div class="msg-row" [class.right]="m.from === 'customer'" [class.left]="m.from === 'rep'"
                    [class.center]="m.from === 'system'">
                    <div class="msg-bubble" [class.customer]="m.from === 'customer'" [class.rep]="m.from === 'rep'"
                        [class.system]="m.from === 'system'">
                        <div class="text">{{ m.text }}</div>
                        <div class="time">{{ m.ts | date:'shortTime' }}</div>
                    </div>
                </div>
                }

                <!-- Typing indicator -->
                @if (svc.repTyping()) {
                <div class="msg-row left">
                    <div class="msg-bubble rep typing">
                        <span class="dots"><span></span><span></span><span></span></span>
                    </div>
                </div>
                }
                <div #messagesEnd></div>
            </div>

            <!-- Composer (enabled after ACCEPT) -->
            @if (svc.composerEnabled() && svc.requestStatus() === 'accepted') {
            <form class="composer" (submit)="send($event)">
                <input type="text" [value]="message()" (focus)="onComposerFocus()" (blur)="onComposerBlur()"
                    (input)="message.set($event.target.value); onTypingInput()"
                    [placeholder]="t('label_type_a_message')" />
                <button class="primary" type="submit" [disabled]="!message().trim()" transloco="label_send"></button>
            </form>
            } @else if (svc.requestStatus() === 'ended') {
            <div class="composer-placeholder" transloco="label_ended"></div>
            }@else {
            <div class="composer-placeholder" transloco="label_waiting"></div>
            }

            }
        </div>
        }
    </section>
    }
    <!-- end:: Full Panel View -->
</ng-container>